import { COMBINED_LINK_DETECTION_PATTERN } from '@/shared/components/text/pattern';
import type {
  LinkType,
  AutoLinkPart,
  NamedGroups
} from '@/shared/components/text/text';

/**
 * Removes common trailing punctuation from a string (only at the end).
 * Returns both the trimmed text and the removed trailing punctuation.
 */
export const trimTrailingPunctuation = (input: string) => {
  const TRAILING_PUNCTUATION_AT_END = /[!),.:;?]+$/; // only at end
  const trimmed = input.replace(TRAILING_PUNCTUATION_AT_END, '');
  const trailing = input.slice(trimmed.length);
  return { trimmed, trailing };
};

/**
 * Normalizes a phone number to digits/plus for tel: scheme.
 */
export const normalizePhoneNumberForTelHref = (rawPhone: string) =>
  rawPhone.replaceAll(/[^\d+]/g, '');

/**
 * Computes href for URL/email/phone types.
 */
export const buildHrefFromMatch = (type: LinkType, value: string) => {
  if (type === 'url') {
    const hasProtocol = /^https?:\/\//i.test(value);
    return hasProtocol ? value : `https://${value}`;
  }
  if (type === 'email') {
    return `mailto:${value}`;
  }
  // phone
  const normalized = normalizePhoneNumberForTelHref(value);
  return `tel:${normalized}`;
};

/**
 * Splits input text into an array of parts:
 * - plain text parts
 * - link parts with type/value/href
 */
export const segmentTextIntoAutoLinkParts = (
  inputText: string = ''
): AutoLinkPart[] => {
  const resultParts: AutoLinkPart[] = [];
  if (!inputText) return resultParts;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = COMBINED_LINK_DETECTION_PATTERN.exec(inputText)) !== null) {
    const matchStartIndex = match.index;
    const matchEndIndex = COMBINED_LINK_DETECTION_PATTERN.lastIndex;

    // Preceding plain text
    if (matchStartIndex > lastIndex) {
      resultParts.push({
        key: `text-${lastIndex}`,
        text: inputText.slice(lastIndex, matchStartIndex)
      });
    }

    const rawMatchedText = match[0];
    const groups = (match.groups || {}) as NamedGroups;

    const linkType: LinkType = groups.url
      ? 'url'
      : // oxlint-disable-next-line unicorn/no-nested-ternary
        groups.email
        ? 'email'
        : 'phone';

    // Avoid including trailing punctuation in the link
    const {
      trimmed: linkTextWithoutTrailingPunctuation,
      trailing: trailingPunctuation
    } = trimTrailingPunctuation(rawMatchedText);

    const value = linkTextWithoutTrailingPunctuation;
    const href = buildHrefFromMatch(linkType, value);

    resultParts.push({
      key: `link-${matchStartIndex}`,
      text: value,
      isLink: true,
      type: linkType,
      href,
      value
    });

    // Trailing punctuation (still render it as plain text)
    if (trailingPunctuation) {
      resultParts.push({
        key: `trail-${matchStartIndex}`,
        text: trailingPunctuation
      });
    }

    lastIndex = matchEndIndex;
  }

  // Remaining tail
  if (lastIndex < inputText.length) {
    resultParts.push({
      key: `text-${lastIndex}`,
      text: inputText.slice(lastIndex)
    });
  }

  return resultParts;
};
